import React, { useState, useEffect, useCallback } from 'react';
import {
    Box,
    Card,
    CardContent,
    CardActions,
    Typography,
    Button,
    Grid,
    Chip,
    IconButton,
    Menu,
    MenuItem,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    FormControl,
    InputLabel,
    Select,
    CircularProgress,
    Alert,
    Tooltip,
} from '@mui/material';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import EditIcon from '@mui/icons-material/Edit';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import DescriptionIcon from '@mui/icons-material/Description';
import EmailIcon from '@mui/icons-material/Email';
import CardGiftcardIcon from '@mui/icons-material/CardGiftcard';
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive';
import { useNotify, useRefresh } from 'react-admin';
import { DocumentTemplate } from './types';
import { generateSlug, makeUniqueSlug } from './slugUtils';
import { buildTemplatesUrl } from './templatesApi';

const baseApiUrl = process.env.REACT_APP_API_URL || 'http://localhost:8000';
const API_URL = baseApiUrl.endsWith('/api') ? baseApiUrl : `${baseApiUrl}/api`;

interface TemplateListProps {
    onEditTemplate: (template: DocumentTemplate) => void;
}

const typeLabels: Record<string, string> = {
    'invoice': 'Rechnung',
    'email': 'E-Mail',
    'certificate': 'Zertifikat',
    'assignment-reminder': 'Zuweisungs-Erinnerung',
};

const typeIcons: Record<string, React.ReactElement> = {
    'invoice': <DescriptionIcon />,
    'email': <EmailIcon />,
    'certificate': <CardGiftcardIcon />,
    'assignment-reminder': <NotificationsActiveIcon />,
};

const typeColors: Record<string, 'primary' | 'secondary' | 'success' | 'warning' | 'info'> = {
    'invoice': 'primary',
    'email': 'info',
    'certificate': 'success',
    'assignment-reminder': 'warning',
};

export const TemplateList: React.FC<TemplateListProps> = ({ onEditTemplate }) => {
    const notify = useNotify();
    const refresh = useRefresh();
    const [templates, setTemplates] = useState<DocumentTemplate[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [filterType, setFilterType] = useState<string>('all');
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const [selectedTemplate, setSelectedTemplate] = useState<DocumentTemplate | null>(null);

    // Create dialog
    const [createDialogOpen, setCreateDialogOpen] = useState(false);
    const [newTemplate, setNewTemplate] = useState({
        name: '',
        type: 'invoice',
        slug: '',
        description: '',
    });
    const [slugTouched, setSlugTouched] = useState(false);
    const [creating, setCreating] = useState(false);

    // Duplicate dialog
    const [duplicateDialogOpen, setDuplicateDialogOpen] = useState(false);
    const [duplicateSlug, setDuplicateSlug] = useState('');
    const [duplicating, setDuplicating] = useState(false);

    // Delete dialog
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [deleting, setDeleting] = useState(false);

    const getToken = () => localStorage.getItem('token');

    const loadTemplates = useCallback(async () => {
        const token = getToken();
        if (!token) return;

        setLoading(true);
        setError(null);

        try {
            const url = buildTemplatesUrl(API_URL, filterType);

            console.log('[TemplateList] Fetching templates from:', url);
            const response = await fetch(url, {
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Accept': 'application/ld+json',
                },
            });

            if (!response.ok) {
                const errorText = await response.text();
                console.error('[TemplateList] Error response:', response.status, errorText);
                throw new Error('Fehler beim Laden der Vorlagen');
            }

            const data = await response.json();
            console.log('[TemplateList] API response:', data);
            // API Platform kann entweder 'hydra:member' oder 'member' zurueckgeben
            const templates = data['hydra:member'] || data['member'] || [];
            console.log('[TemplateList] Templates found:', templates.length);
            setTemplates(templates);
        } catch (err) {
            console.error('Error loading templates:', err);
            setError('Fehler beim Laden der Vorlagen');
        } finally {
            setLoading(false);
        }
    }, [filterType]);

    useEffect(() => {
        loadTemplates();
    }, [loadTemplates]);

    const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, template: DocumentTemplate) => {
        setAnchorEl(event.currentTarget);
        setSelectedTemplate(template);
    };

    const handleMenuClose = () => {
        setAnchorEl(null);
        setSelectedTemplate(null);
    };

    const handleCreateTemplate = async () => {
        const token = getToken();
        if (!token) return;

        setCreating(true);
        try {
            const response = await fetch(`${API_URL}/document_templates`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/ld+json',
                    'Accept': 'application/ld+json',
                },
                body: JSON.stringify(newTemplate),
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.detail || 'Fehler beim Erstellen der Vorlage');
            }

            const createdTemplate = await response.json();
            notify('Vorlage erfolgreich erstellt', { type: 'success' });
            setCreateDialogOpen(false);
            setNewTemplate({ name: '', type: 'invoice', slug: '', description: '' });
            loadTemplates();
            onEditTemplate(createdTemplate);
        } catch (err: any) {
            console.error('Error creating template:', err);
            notify(err.message || 'Fehler beim Erstellen der Vorlage', { type: 'error' });
        } finally {
            setCreating(false);
        }
    };

    const handleDuplicate = async () => {
        if (!selectedTemplate) return;
        const token = getToken();
        if (!token) return;

        setDuplicating(true);
        try {
            const response = await fetch(`${API_URL}/document-templates/${selectedTemplate.id}/duplicate`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json',
                    'Accept': 'application/json',
                },
                body: JSON.stringify({ slug: duplicateSlug }),
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.error || 'Fehler beim Duplizieren');
            }

            const result = await response.json();
            notify('Vorlage erfolgreich dupliziert', { type: 'success' });
            setDuplicateDialogOpen(false);
            setDuplicateSlug('');
            loadTemplates();
            onEditTemplate(result.template);
        } catch (err: any) {
            console.error('Error duplicating template:', err);
            notify(err.message || 'Fehler beim Duplizieren', { type: 'error' });
        } finally {
            setDuplicating(false);
            handleMenuClose();
        }
    };

    const handleDelete = async () => {
        if (!selectedTemplate) return;
        const token = getToken();
        if (!token) return;

        setDeleting(true);
        try {
            const response = await fetch(`${API_URL}/document_templates/${selectedTemplate.id}`, {
                method: 'DELETE',
                headers: {
                    'Authorization': `Bearer ${token}`,
                },
            });

            if (!response.ok) {
                throw new Error('Fehler beim Löschen');
            }

            notify('Vorlage erfolgreich geloescht', { type: 'success' });
            setDeleteDialogOpen(false);
            loadTemplates();
        } catch (err: any) {
            console.error('Error deleting template:', err);
            notify(err.message || 'Fehler beim Löschen', { type: 'error' });
        } finally {
            setDeleting(false);
            handleMenuClose();
        }
    };

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Box>
            {/* Header — flexWrap: bricht auf schmalen Viewports um (#36-Sweep) */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexWrap: 'wrap', gap: 1 }}>
                <FormControl size="small" sx={{ minWidth: 200 }}>
                    <InputLabel>Typ filtern</InputLabel>
                    <Select
                        value={filterType}
                        label="Typ filtern"
                        onChange={(e) => setFilterType(e.target.value)}
                    >
                        <MenuItem value="all">Alle Typen</MenuItem>
                        <MenuItem value="invoice">Rechnungen</MenuItem>
                        <MenuItem value="email">E-Mails</MenuItem>
                        <MenuItem value="certificate">Zertifikate</MenuItem>
                        <MenuItem value="assignment-reminder">Zuweisungs-Erinnerungen</MenuItem>
                    </Select>
                </FormControl>
                <Button
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={() => {
                        setNewTemplate({ name: '', type: 'invoice', slug: '', description: '' });
                        setSlugTouched(false);
                        setCreateDialogOpen(true);
                    }}
                >
                    Neue Vorlage
                </Button>
            </Box>

            {error && (
                <Alert severity="error" sx={{ mb: 2 }}>
                    {error}
                </Alert>
            )}

            {/* Template Grid */}
            <Grid container spacing={3}>
                {templates.map((template) => (
                    <Grid size={{ xs: 12, sm: 6, md: 4 }} key={template.id}>
                        <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column', cursor: 'pointer', transition: 'box-shadow 0.2s', '&:hover': { boxShadow: 4 } }} onClick={() => onEditTemplate(template)}>
                            {/* HTML Preview Thumbnail */}
                            {template.contentHtml && (
                                <Box sx={{ height: 120, overflow: 'hidden', bgcolor: '#fafafa', borderBottom: '1px solid #eee', position: 'relative' }}>
                                    <Box sx={{ transform: 'scale(0.25)', transformOrigin: 'top left', width: '400%', height: '400%', pointerEvents: 'none' }}>
                                        <iframe
                                            sandbox=""
                                            srcDoc={template.contentHtml.substring(0, 2000)}
                                            title="Vorschau"
                                            style={{ width: '100%', height: '100%', border: 'none' }}
                                        />
                                    </Box>
                                    <Box sx={{ position: 'absolute', bottom: 0, left: 0, right: 0, height: 30, background: 'linear-gradient(transparent, #fafafa)' }} />
                                </Box>
                            )}
                            <CardContent sx={{ flexGrow: 1 }}>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        {typeIcons[template.type]}
                                        <Chip
                                            label={typeLabels[template.type] || template.type}
                                            size="small"
                                            color={typeColors[template.type] || 'default'}
                                        />
                                    </Box>
                                    <IconButton
                                        size="small"
                                        onClick={(e) => { e.stopPropagation(); handleMenuOpen(e, template); }}
                                    >
                                        <MoreVertIcon />
                                    </IconButton>
                                </Box>

                                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                                    {template.name}
                                </Typography>

                                {template.description && (
                                    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                                        {template.description.length > 80 ? template.description.substring(0, 80) + '...' : template.description}
                                    </Typography>
                                )}

                                <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mt: 1.5 }}>
                                    {template.isDefault && <Chip label="Standard" size="small" color="primary" variant="outlined" />}
                                    <Chip label={`v${template.currentVersionNumber}`} size="small" variant="outlined" />
                                    {template.updatedAt && (
                                        <Typography variant="caption" color="text.secondary" sx={{ alignSelf: 'center', ml: 'auto' }}>
                                            {new Date(template.updatedAt).toLocaleDateString('de-DE')}
                                        </Typography>
                                    )}
                                </Box>
                            </CardContent>
                        </Card>
                    </Grid>
                ))}
            </Grid>

            {templates.length === 0 && !error && (
                <Box sx={{ textAlign: 'center', py: 8 }}>
                    <Typography variant="h6" color="text.secondary" gutterBottom>
                        Keine Vorlagen gefunden
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        Erstellen Sie Ihre erste Vorlage um loszulegen.
                    </Typography>
                </Box>
            )}

            {/* Context Menu */}
            <Menu
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={handleMenuClose}
            >
                <MenuItem onClick={() => {
                    handleMenuClose();
                    if (selectedTemplate) onEditTemplate(selectedTemplate);
                }}>
                    <EditIcon sx={{ mr: 1 }} /> Bearbeiten
                </MenuItem>
                <MenuItem onClick={() => {
                    setDuplicateSlug(selectedTemplate?.slug + '-copy' || '');
                    setDuplicateDialogOpen(true);
                }}>
                    <ContentCopyIcon sx={{ mr: 1 }} /> Duplizieren
                </MenuItem>
                <MenuItem
                    onClick={() => setDeleteDialogOpen(true)}
                    sx={{ color: 'error.main' }}
                >
                    <DeleteIcon sx={{ mr: 1 }} /> Löschen
                </MenuItem>
            </Menu>

            {/* Create Dialog */}
            <Dialog open={createDialogOpen} onClose={() => setCreateDialogOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle>Neue Vorlage erstellen</DialogTitle>
                <DialogContent>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
                        <TextField
                            label="Name"
                            value={newTemplate.name}
                            onChange={(e) => {
                                const name = e.target.value;
                                setNewTemplate(prev => ({
                                    ...prev,
                                    name,
                                    slug: slugTouched
                                        ? prev.slug
                                        : makeUniqueSlug(generateSlug(name), templates.map(t => t.slug)),
                                }));
                            }}
                            fullWidth
                            required
                        />
                        <FormControl fullWidth>
                            <InputLabel>Typ</InputLabel>
                            <Select
                                value={newTemplate.type}
                                label="Typ"
                                onChange={(e) => setNewTemplate(prev => ({ ...prev, type: e.target.value }))}
                            >
                                <MenuItem value="invoice">Rechnung</MenuItem>
                                <MenuItem value="email">E-Mail</MenuItem>
                                <MenuItem value="certificate">Zertifikat</MenuItem>
                                <MenuItem value="assignment-reminder">Zuweisungs-Erinnerung</MenuItem>
                            </Select>
                        </FormControl>
                        <TextField
                            label="Slug"
                            value={newTemplate.slug}
                            onChange={(e) => {
                                setSlugTouched(true);
                                setNewTemplate(prev => ({ ...prev, slug: e.target.value }));
                            }}
                            fullWidth
                            required
                            helperText="Eindeutiger Bezeichner (z.B. order-confirmation)"
                        />
                        <TextField
                            label="Beschreibung"
                            value={newTemplate.description}
                            onChange={(e) => setNewTemplate(prev => ({ ...prev, description: e.target.value }))}
                            fullWidth
                            multiline
                            rows={2}
                        />
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setCreateDialogOpen(false)}>Abbrechen</Button>
                    <Button
                        onClick={handleCreateTemplate}
                        variant="contained"
                        disabled={!newTemplate.name || !newTemplate.slug || creating}
                    >
                        {creating ? <CircularProgress size={24} /> : 'Erstellen'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Duplicate Dialog */}
            <Dialog open={duplicateDialogOpen} onClose={() => setDuplicateDialogOpen(false)}>
                <DialogTitle>Vorlage duplizieren</DialogTitle>
                <DialogContent>
                    <TextField
                        label="Neuer Slug"
                        value={duplicateSlug}
                        onChange={(e) => setDuplicateSlug(e.target.value)}
                        fullWidth
                        sx={{ mt: 1 }}
                        helperText="Eindeutiger Bezeichner fuer die Kopie"
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDuplicateDialogOpen(false)}>Abbrechen</Button>
                    <Button
                        onClick={handleDuplicate}
                        variant="contained"
                        disabled={!duplicateSlug || duplicating}
                    >
                        {duplicating ? <CircularProgress size={24} /> : 'Duplizieren'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Delete Dialog */}
            <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
                <DialogTitle>Vorlage löschen?</DialogTitle>
                <DialogContent>
                    <Typography>
                        Möchten Sie die Vorlage "{selectedTemplate?.name}" wirklich löschen?
                        Diese Aktion kann nicht rueckgaengig gemacht werden.
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDeleteDialogOpen(false)}>Abbrechen</Button>
                    <Button
                        onClick={handleDelete}
                        color="error"
                        variant="contained"
                        disabled={deleting}
                    >
                        {deleting ? <CircularProgress size={24} /> : 'Löschen'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default TemplateList;
